import React, { PureComponent } from 'react'
import { FormattedMessage, FormattedDate, FormattedTime } from 'react-intl'
import bwipjs from 'bwip-js'
import * as Sentry from '@sentry/browser'
import Spinner from '../components/Spinner'
import AuthenticatedAPI from '../lib/authenticatedAPI'
import { fixLegacyData } from '../lib/booking'
import ErrorBlock from '../components/ErrorBlock'
import Button from '../components/Button'

const generateBarCode = bookingOrder => {
  if (bookingOrder.membership) {
    bwipjs(`canvas-${bookingOrder.id}`, {
      bcid: 'qrcode',       // Barcode type
      text: bookingOrder.axess && bookingOrder.axess.orderNo,    // Text to encode
      scale: 2,               // 3x scaling factor
      height: 15,              // Bar height, in millimeters
      includetext: true,            // Show human-readable text
      textxalign: 'center',        // Always good to set this
    }, (err, cvs) => {
      if (err) {
        // Decide how to handle the error
        // `err` may be a string or Error object
      } else {
        // Nothing else to do in this example...
      }
    });
  }
}

class BookingDetails extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      booking: {},
      isLoading: true
    }
    // this.gotoBooking = this.gotoBooking.bind(this)
    this.getKey = this.getKey.bind(this)  
  }

  componentWillMount() {
    window.scrollTo(0, 0); // TODO avoid duplication on all pages
  }

  async componentDidMount() {
    const { props } = this
    
    const bookingId = parseInt(props.match.params.id, 10)

    try {     
      let booking = await AuthenticatedAPI.get('cphill', `/bookings/${bookingId}`)
  
      await fixLegacyData(booking);
      booking = await AuthenticatedAPI.get('cphill', `/bookings/${bookingId}`)

      const bookingOrdersAwaitingBarcode = booking.bookingOrders
        .filter(order => !order.axess || !order.axess.status || order.axess.status === 'AWAITING_BARCODE_ISSUANCE' || (order.membership && order.membership.passLost))
  
      if (bookingOrdersAwaitingBarcode.length > 0) {
        await Promise.all(bookingOrdersAwaitingBarcode.map(order => 
          AuthenticatedAPI.put('cphill', `/bookings/${bookingId}/orders/${order.id}`, {})
        ))
        booking = await AuthenticatedAPI.get('cphill', `/bookings/${bookingId}`)
      }
      const bookingOrdersAwaitingCodeScan = booking.bookingOrders.filter(order => order.axess && order.axess.status === 'AWAITING_CARD_ISSUANCE')
      
      this.setState({
        booking,
        bookingOrdersAwaitingCodeScan,
        isLoading: false
      })
  
      bookingOrdersAwaitingCodeScan.forEach(bookingOrder => {
        generateBarCode(bookingOrder)
      })
    } catch (error) {
      Sentry.captureException(error)
      this.setState({ errorMsg: "There was an issue with the connection", isLoading: false })
      // eslint-disable-next-line no-console
      console.error(error)
    }
  }

  getKey() {
    this.keyCount += 1
    return this.keyCount
  }

  errorBlock = (errorMsg) => (
    <div className="container mx-auto">
      <ErrorBlock headerText="An error occured" descriptionText={errorMsg} />
      <div className="flex items-center mt-4">
        <div>
          Click here to&nbsp;
        </div>
        <Button type="button" sizeName="sm" onClick={() => { window.location.reload(true) }} className="font-bold" type="button">
          reload
        </Button>
        <div>
          &nbsp;page
        </div>
      </div>
    </div>
  )

  render() {
    const { isLoading, booking, bookingOrdersAwaitingCodeScan, errorMsg } = this.state

    if (errorMsg) {
      return this.errorBlock(errorMsg)
    }

    return (
      <>
        <div className="container mx-auto md:w-2/3 lg:w-1/2 md:mt-8 mb-12 px-4 md:px-0">
          <h4 className="text-harbor-80 uppercase text-sky">
            <FormattedMessage
              id="booking.details.category"
              defaultMessage="Booking"
            />
          </h4>

          <h1>
            <FormattedMessage
              id="booking.details.header"
              defaultMessage="Booking details"
            />
          </h1>

          {isLoading || (!booking.dateStart) ? (
            <Spinner />
          ) : (
            <>
              <div className="border-b pb-4">
                <div className="font-bold">
                  <FormattedDate
                    value={booking.dateStart}
                    year='numeric'
                    month='long'
                    day='numeric'
                  />
                </div>
                <div className="text-xs font-bold text-sky">
                  <FormattedTime value={booking.dateStart} />
                  &nbsp;-&nbsp;
                  <FormattedTime value={booking.dateEnd} />
                </div>
                <div className="text-xs">
                  {bookingOrdersAwaitingCodeScan.length}
                  &nbsp;
                  {bookingOrdersAwaitingCodeScan.length === 1 ? 'person' : 'personer'}
                </div>
              </div>
              <p className="bg-grass mt-4 p-2 text-xs">
                Nedenfor finder du dine stregkoder som du skal scanne i vores billetautomat.
                <br />
                <span className="font-bold">
                  OBS:
                </span>
                &nbsp;Der er 1 kode til hver liftkort som skal udstedes.
                <br />
                Du&nbsp;
                <span className="underline">kan godt</span>
                &nbsp;scanne fra din mobiltelefon.
              </p>
        
              <div className="w-full scrolling-touch overflow-x-scroll">
                <div
                  className="flex mt-4"
                  style={{ width: `${bookingOrdersAwaitingCodeScan.length * 100}%`}}
                >
                  {bookingOrdersAwaitingCodeScan.map((order, index) => (
                    <div 
                      className="text-center w-screen pr-4"
                      key={this.getKey()}
                    >
                      {bookingOrdersAwaitingCodeScan.length > 1 && (
                        <div className="flex my-4 justify-center">
                          {bookingOrdersAwaitingCodeScan.map((bo, i) => (
                            <div className={`${i === index && 'border-blue-darkest'} flex justify-center items-center border rounded-full h-6 w-6 mx-1`}>
                              <div className={`${i === index && 'bg-blue-darkest'} rounded-full h-3 w-3`} />
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="border rounded-lg pb-16">
                        <div className="uppercase font-bold text-sky mt-8">
                          {`${order.person.firstName} ${order.person.lastName}`}
                          {/* <br />
                          {order.axess && order.axess.status} */}
                          {/* Deltager,&nbsp;
                          {index+1} */}
                        </div>
                        <h2 className="mb-8">
                          {order.membership && (
                            order.membership.type.includes('Adult')
                              ? 'Voksen'
                              : 'Barn'
                          )}
                        </h2>

                        <canvas id={`canvas-${order.id}`} />
                        {/* {order.membership && order.membership.axessWTP} */}
                      </div>
                    </div>  
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </>
    )
  }
}

BookingDetails.propTypes = {
  // booking: PropTypes.shape({ booking: PropTypes.object }).isRequired
}

// const mapStateToProps = state => ({
//   booking: state.booking
// })

export default BookingDetails